import React, { useState } from 'react'
import { useMutation, queryCache } from 'react-query'
import { useForm } from 'react-hook-form'
import { useStateMachine } from 'little-state-machine'
import styled from 'styled-components'
import { useWithAuth } from '../App'
import { inquiryStyles } from '../../components/Select/styles'
import { updateInquery, initialStateAction } from './actions'
import { useNotifications } from '../../containers/Notifications'
//import { MAX_LENGTH } from '../../components/Scales/CharCounter'
import { ID } from '../../types/id'
import {
  ReportsStyledButton,
  ReportsTextArea,
  ReportsInput,
  ReportsFormWrapper,
  ReportsStyledWrapper,
  ReportsStyledReactSelect,
  ReportsSelectGridWrapper,
} from '../../styles/Shared'

import {
  ReportInquery,
  normScales,
  ReportInqueryStore,
  confidenceInterval,
} from './utils'
import Checkbox from '../../components/Checkbox'

type Props = {
  reportType: string
  code: string
  patientFirstName: string
  patientLastName: string
  id: ID
  onClose: () => void
}

const ReportsScalesForm = ({
  id,
  reportType,
  onClose,
  code,
  patientFirstName,
  patientLastName,
}: Props) => {
  React.useEffect(() => {
    actions.initial({})
    return () => {
      actions.initial({})
    }
  }, [])
  const [afterSubmit, setAfterSubmit] = useState<boolean>(false)
  const icfDaysRequired = 1050
  const patientAgeAtDiagnosis = parseInt(code.slice(3, 8))
  const { actions, state: inqueryState } = useStateMachine<ReportInqueryStore>({
    update: updateInquery,
    initial: initialStateAction,
  })

  const form = useForm<ReportInquery>({})
  const endpoint = `/report/rest/examination/${id}/${reportType}`
  const withAuth = useWithAuth()
  const { notify } = useNotifications()

  const normalizedValues = React.useMemo(() => {
    return {
      ...inqueryState.inqueryReports,
      name:
        inqueryState.inqueryReports.name === ''
          ? 'Raport'
          : inqueryState.inqueryReports.name,
      normScale: inqueryState.inqueryReports.normScale.value,
      confidenceInterval: inqueryState.inqueryReports.confidenceInterval.value,
      firstName: patientFirstName,
      lastName: patientLastName,
    }
  }, [afterSubmit])

  React.useEffect(() => {
    afterSubmit && mutate(normalizedValues)
  }, [normalizedValues, afterSubmit])

  React.useEffect(() => {
    !(patientAgeAtDiagnosis > icfDaysRequired)
      ? actions.update({
          icfReferenceVisible: false,
        })
      : null
  }, [])

  const [mutate] = useMutation<any, ReportInquery>(
    (values) =>
      withAuth(endpoint, {
        method: 'POST',
        payload: values,
      })
        .then((res) => {
          if ('fieldErrors' in res) {
            // @todo notifications
          } else {
            queryCache.refetchQueries(`reports-${id}`)
            notify(
              'Dodano zlecenie wygenerowania raportu dla skal opisowych',
              'success'
            )
            setAfterSubmit(false)
            onClose()
          }
        })
        .catch(console.log),

    {}
  )

  const handleSubmit = form.handleSubmit((values) => {
    actions.update(values)
    setAfterSubmit(true)
  })

  return (
    <ReportsStyledWrapper onSubmit={handleSubmit}>
      <h2>Szczegóły Raportu</h2>
      <ReportsFormWrapper>
        <div style={{ width: '97.5%' }}>
          <label>
            Nazwa
            <ReportsInput ref={form.register} name="name" />
          </label>
        </div>
        <ReportsSelectGridWrapper oneRow>
          <label>
            Skala
            <ReportsStyledReactSelect
              defaultValue={inqueryState.inqueryReports.normScale}
              onChange={(option) => actions.update({ normScale: option })}
              styles={inquiryStyles}
              options={normScales.map((option) => option)}
            ></ReportsStyledReactSelect>
          </label>
          <label>
            Przedział ufności
            <ReportsStyledReactSelect
              defaultValue={inqueryState.inqueryReports.confidenceInterval}
              onChange={(option) =>
                actions.update({ confidenceInterval: option })
              }
              styles={inquiryStyles}
              options={confidenceInterval.map((option) => option)}
            ></ReportsStyledReactSelect>
          </label>
        </ReportsSelectGridWrapper>
        <ReportsStyledCheckbox
          label={'Dane podstawowe'}
          input={{
            value: inqueryState.inqueryReports.personalDataVisible,
            onChange: () =>
              actions.update({
                personalDataVisible: !inqueryState.inqueryReports
                  .personalDataVisible,
              }),
          }}
        />
        <ReportsStyledCheckbox
          label={'Informacje o osobach wypełniających skalę'}
          input={{
            value: inqueryState.inqueryReports.respondentsDataVisible,
            onChange: () =>
              actions.update({
                respondentsDataVisible: !inqueryState.inqueryReports
                  .respondentsDataVisible,
              }),
          }}
        />
        <ReportsStyledCheckbox
          label={'Odniesienie wyników do populacji ogólnej'}
          input={{
            value:
              inqueryState.inqueryReports.resultsRelatedToPopulationVisible,
            onChange: () =>
              actions.update({
                resultsRelatedToPopulationVisible: !inqueryState.inqueryReports
                  .resultsRelatedToPopulationVisible,
              }),
          }}
        />
        <ReportsStyledCheckbox
          label={'Odniesienie wyników do grupy SPE'}
          input={{
            value:
              inqueryState.inqueryReports.resultsRelatedToDisabilityVisible,
            onChange: () =>
              actions.update({
                resultsRelatedToDisabilityVisible: !inqueryState.inqueryReports
                  .resultsRelatedToDisabilityVisible,
              }),
          }}
        />
        <ReportsStyledCheckbox
          label={'Protokół wypełnienia kwestionariusza'}
          input={{
            value: inqueryState.inqueryReports.inquiriesProtocolVisible,
            onChange: () =>
              actions.update({
                inquiriesProtocolVisible: !inqueryState.inqueryReports
                  .inquiriesProtocolVisible,
              }),
          }}
        />
        <ReportsStyledCheckbox
          label={'Odniesienie wyników do ICF'}
          disabled={!(patientAgeAtDiagnosis > icfDaysRequired)}
          input={{
            value: inqueryState.inqueryReports.icfReferenceVisible,
            onChange: () =>
              actions.update({
                icfReferenceVisible: !inqueryState.inqueryReports
                  .icfReferenceVisible,
              }),
          }}
        />
        <ReportsStyledCheckbox
          label={'Proponowane materiały postdiagnostyczne'}
          input={{
            value: inqueryState.inqueryReports.postdiagnosticVisible,
            onChange: () =>
              actions.update({
                postdiagnosticVisible: !inqueryState.inqueryReports
                  .postdiagnosticVisible,
              }),
          }}
        />
        <ReportsStyledCheckbox
          label={'Interpretacja wyników'}
          input={{
            value: inqueryState.inqueryReports.resultsInterpretationVisible,
            onChange: () =>
              actions.update({
                resultsInterpretationVisible: !inqueryState.inqueryReports
                  .resultsInterpretationVisible,
              }),
          }}
        />
        <div style={{ width: '100%' }}>
          <label>
            Pole tekstowe
            <ReportsTextArea
              readOnly={
                !inqueryState.inqueryReports.resultsInterpretationVisible
              }
              ref={form.register}
              name="resultsInterpretation"
            />
          </label>
        </div>
        <div style={{ width: '100%' }}>
          <ReportsStyledButton color="white" label="Anuluj" onClick={onClose} />
          <ReportsStyledButton
            style={{ marginLeft: '10%' }}
            label="Generuj"
            type="submit"
          />
        </div>
      </ReportsFormWrapper>
    </ReportsStyledWrapper>
  )
}

const ReportsStyledCheckbox = styled(Checkbox)`
  margin: 10px 0;

  label {
    flex-direction: row;
    font-weight: 100;
  }
`

export default ReportsScalesForm
